import { Box, Grid, styled, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { useHideLimitedCompanyShiftFields } from 'ogp/limited-company-capabilities';
import React, { useMemo } from 'react';
import { CalendarDayColumn } from './calendar-day-column';
import { useGetVisibleDays } from '../../hooks/use-get-visible-days';
import { CalendarPlanningShift } from '../calendar-planning-shift.types';
import { buildCalendarShiftColumnLayout } from '../../helpers/build-calendar-shift-column-layout';

type CalendarJobTypeProps = {
  jobType?: {
    id: string;
    name: string;
    shifts: CalendarPlanningShift[];
  };
};

export const CalendarJobType = React.memo(
  ({ jobType }: CalendarJobTypeProps) => {
    const hideLimitedCompanyShiftFields = useHideLimitedCompanyShiftFields();
    const { visibleDays } = useGetVisibleDays();
    const columnLayout = useMemo(
      () => (jobType != null ? buildCalendarShiftColumnLayout(jobType.shifts, visibleDays) : {}),
      [jobType, visibleDays]
    );

    return (
      <JobWrapper>
        {hideLimitedCompanyShiftFields ? (
          <JobName aria-hidden />
        ) : (
          <JobName title={jobType ? jobType.name : ''}>{jobType ? jobType.name : null}</JobName>
        )}
        <JobShiftGrid container flexWrap={'nowrap'}>
          {visibleDays.map((date) => {
            const dayKey = DateTime.fromISO(date).toFormat('dd-LL');
            const columnData = columnLayout[dayKey] ?? [];

            return (
              <CalendarDayColumn
                key={date}
                dayKey={dayKey}
                data={columnData}
                dayCount={visibleDays.length}
              />
            );
          })}
        </JobShiftGrid>
      </JobWrapper>
    );
  },
  (prev, next) => {
    if (prev.jobType?.id !== next.jobType?.id) {
      return false;
    }
    if (prev.jobType?.shifts.length !== next.jobType?.shifts.length) {
      return false;
    }
    for (let i = 0; i < (prev.jobType?.shifts.length ?? 0); i++) {
      if (prev.jobType?.shifts[i].id !== next.jobType?.shifts[i].id) {
        return false;
      }
      if (JSON.stringify(prev.jobType?.shifts[i]) !== JSON.stringify(next.jobType?.shifts[i])) {
        return false;
      }
    }
    return true;
  }
);

const JobWrapper = styled(Box)(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.grey[200]}`,
  display: 'flex',
}));

const JobShiftGrid = styled(Grid)({
  '& > .MuiGrid-item:nth-child(2n - 1)': {
    background: 'white',
  },
});

const JobName = styled(Typography)(({ theme }) => ({
  width: '14vw',
  padding: theme.spacing(2, 2, 2, 0),
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  flex: 'none',
  whiteSpace: 'nowrap',
}));
